import lodash, { first, get, isEmpty, size } from 'lodash'
import React, {
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import sleep from 'sleep-promise'
import styled from 'styled-components'
import { v4 as uuid } from 'uuid'

import { useFieldContext } from '../../../../1stlog-ui/src/components/Field'
import Icon from '../../../../1stlog-ui/src/components/Icon'
import Spinner from '../../../../1stlog-ui/src/components/Spinner'
import { usePrevious } from '../../../../1stlog-ui/src/hooks'
import { IAdvancedAddress } from '../../../../1stlog-ui/src/types'
import useFieldJumper from '../../OrderEntry/Form/FieldJumper/useFieldJumper'
import { IOrderEntryAddress } from '../../OrderEntry/types'
import useOrderEntry from '../../OrderEntry/useOrderEntry'
import { requestAddressSearch } from './requests'
import SuggestionPopup from './SuggestionPopup'
import { TAddressSearchSuggestion } from './types'
import useAddressSearchPayload from './useAddressSearchPayload'

const Wrapper = styled.div`
  // min-height: 30px;
`

function trimAddress(
  address: IOrderEntryAddress | undefined,
): IAdvancedAddress {
  return lodash.omit(address, [
    'showContact',
    'driverInstructions',
    'customerAddressHint',
  ])
}

type TAddressSuggestionsProps = {
  currentAddress?: IOrderEntryAddress
  debounceDelay?: number
  onApply?: (values: any) => void
  appliedAddress?: IOrderEntryAddress
}

const AddressSuggestions = (props: TAddressSuggestionsProps): ReactElement => {
  const {
    currentAddress,
    debounceDelay = 500,
    onApply = lodash.noop,
    appliedAddress,
  } = props

  const [isInit, setIsInit] = useState(true)
  const [isLoading, setIsLoading] = useState(false)
  const [isPopupOpen, setIsPopupOpen] = useState(false)
  const [suggestions, setSuggestions] = useState<IAdvancedAddress[] | null>(
    null,
  )
  const [focusedElement, setFocusedElement] = useState<string | undefined>(
    undefined,
  )
  const isName1Focused = useMemo(
    () => focusedElement && /name1/.test(focusedElement),
    [focusedElement],
  )

  // TODO: if form is empty, set isInit to true

  const focusHandler = (event: FocusEvent): void => {
    const name = get(event, 'target.name')
    const type = get(event, 'target.nodeName', '')?.toLowerCase()
    if (type === 'input') {
      setFocusedElement(name)
    }
  }

  const blurHandler = (event: FocusEvent): void => {
    const name = get(event, 'target.name')
    const type = get(event, 'target.nodeName', '')?.toLowerCase()
    if (type === 'input') {
      setFocusedElement(undefined)
    }
  }

  useEffect(() => {
    document.body.addEventListener('focus', focusHandler, true)
    document.body.addEventListener('blur', blurHandler, true)
    return () => {
      document.body.removeEventListener('focus', focusHandler, true)
      document.body.removeEventListener('blur', blurHandler, true)
    }
  }, [])

  const { prefix = '' } = useFieldContext()

  const { addressbookUrl, tenantId } = useOrderEntry()

  const openPopup = (): void => {
    setTimeout(() => setIsPopupOpen(true), 0)
  }
  const closePopup = (): void => setIsPopupOpen(false)

  const wasJustSet = useMemo(() => {
    return lodash.isEqual(
      trimAddress(appliedAddress),
      trimAddress(currentAddress),
    )
  }, [currentAddress, appliedAddress])

  const address = useAddressSearchPayload()

  const applyAddress = (values: IAdvancedAddress): void => {
    if (!values) {
      return
    }
    onApply(values)
    closePopup()
  }

  const lastUuid = useRef<string>()

  const loadSuggestions = async (values: IOrderEntryAddress): Promise<void> => {
    setIsInit(false)
    const thisUuid = uuid()
    lastUuid.current = thisUuid
    let result: TAddressSearchSuggestion[] | null = null
    try {
      await sleep(debounceDelay)
      if (thisUuid !== lastUuid.current) {
        return
      }
      setIsLoading(true)
      result = await requestAddressSearch(values, {
        url: addressbookUrl,
        customerId: tenantId,
      })
    } catch (err) {
      console.error('Failed to handle address suggestion request', err)
    } finally {
      // check if its still the latest request
      if (thisUuid === lastUuid.current) {
        setSuggestions(result)
        setIsLoading(false)
      }
    }
  }

  const isInitial = useRef(true)
  const previousAddress = usePrevious(address)
  useEffect(() => {
    // prevent search on mount
    if (isInitial.current) {
      isInitial.current = false
      return
    }
    if (!address) {
      setSuggestions(null)
    }
    const allowFalse = true
    if (
      !!address &&
      !lodash.isEqual(previousAddress, address) &&
      (allowFalse || !lodash.some(lodash.values(address), (v) => v === false))
    ) {
      loadSuggestions(address)
    }
  }, [address])

  const onKeyPress = useCallback(
    (e): void => {
      if (e.key !== 'Enter') {
        return
      }
      if (wasJustSet) {
        return
      }
      // ensure that this event is from the current address form
      const activeName = String(lodash.get(document.activeElement, 'name'))
      if (!activeName.includes(prefix)) {
        return
      }
      if (!/name1/.test(activeName)) {
        // console.log('onEnter', { suggestions })
        if (!isEmpty(suggestions)) {
          if (size(suggestions) === 1) {
            applyAddress(first(suggestions) as IAdvancedAddress)
          } else {
            setTimeout(() => openPopup(), 0) // To prevent onKey event in popup
          }
        }
      }
    },
    [suggestions, wasJustSet],
  )

  useEffect(() => {
    window.addEventListener('keypress', onKeyPress)

    return () => {
      window.removeEventListener('keypress', onKeyPress)
    }
  }, [onKeyPress])

  const { disable, enable } = useFieldJumper()

  useEffect(() => {
    if (!isEmpty(suggestions)) {
      // console.log('disable field jumper')
      disable()
    }
    if (wasJustSet || isEmpty(suggestions)) {
      // console.log('enable field jumper')
      enable()
    }
  }, [wasJustSet, suggestions])

  return (
    <Wrapper className="mb-2">
      <SuggestionPopup
        isOpen={isPopupOpen}
        addresses={suggestions}
        onHide={closePopup}
        onSelect={applyAddress}
        title={i18n.t('title_select_address', 'Adresse auswählen')}
      />
      {/* <div>ELEMENT: {focusedElement}</div> */}
      {wasJustSet ? (
        <div className="text-success mt-0">
          <Icon icon="far fa-check" />{' '}
          {i18n.t('address_was_applied', 'Adresse wurde übernommen')}
        </div>
      ) : (
        <>
          {isName1Focused && (
            <div className="text-muted">
              {i18n.t(
                'start_typing_to_search_addresses',
                'Fangen Sie an zu tippen, um nach Adressen zu suchen.',
              )}
            </div>
          )}
          {!isName1Focused && isLoading && (
            <div className="d-flex">
              <div className="mr-1">
                <Spinner />
              </div>
              <div>
                <div className="mt-0">
                  {i18n.t(
                    'addresses_being_searched',
                    'Adressen werden durchsucht',
                  )}
                  ...
                </div>
              </div>
            </div>
          )}
          {!isName1Focused && !isLoading && suggestions === undefined && (
            <div className="text-muted">
              {i18n.t(
                'type_to_automatically_search',
                'Tippen Sie, um automatisch zu Suchen',
              )}
              .
            </div>
          )}
          {!isName1Focused &&
            !isLoading &&
            suggestions !== undefined &&
            lodash.isEmpty(suggestions) && (
              <div className="text-muted">
                {isInit
                  ? i18n.t(
                      'fill_any_fields_to_search_addresses',
                      'Beliebige Felder ausfüllen, um Adressen zu suchen',
                    )
                  : i18n.t('no_addresses_found', 'Keine Adressen gefunden')}
                .
              </div>
            )}
          {!isName1Focused && !isLoading && !lodash.isEmpty(suggestions) && (
            <div>
              <div className="text-success m-0">
                {lodash.size(suggestions) > 1
                  ? i18n.t('x_addresses_found_plural', {
                      defaultValue: '{{count}} Adressen gefunden',
                      count: lodash.size(suggestions),
                    })
                  : i18n.t('x_addresses_found', {
                      defaultValue: '{{count}} Adresse gefunden',
                      count: lodash.size(suggestions),
                    })}
                .
              </div>
              <div className="text-muted">
                {focusedElement && !isEmpty(suggestions) && (
                  <div>
                    {size(suggestions) > 1 ? (
                      <div>
                        {i18n.t(
                          'press_enter_to_show_suggested_addresses',
                          'Enter drücken, um alle zutreffenden Adressen anzuzeigen',
                        )}
                        .
                      </div>
                    ) : (
                      <div>
                        {i18n.t(
                          'press_enter_to_apply_suggested_address',
                          'Enter drücken, um die zutreffende Adresse zu übernehmen',
                        )}
                        .
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </>
      )}
    </Wrapper>
  )
}

export default AddressSuggestions
